import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '../Icon';
import { ReceiptItemsEditor, ReceiptItem } from '../ReceiptItemsEditor';
import { UnifiedToggle } from '../UnifiedToggle';
import { apiClient } from '../../api/client';
import { Category } from '../../types';
import { localizeCategory } from '../../utils/format';
import './TransactionEditModal.css';

interface TransactionEditModalProps {
  transaction: any;
  isSubmitting: boolean;
  onUpdate: (transaction: any) => void;
  onDelete: () => void;
  onClose: () => void;
}

export const TransactionEditModal: React.FC<TransactionEditModalProps> = ({
  transaction,
  isSubmitting,
  onUpdate,
  onDelete,
  onClose
}) => {
  const { t } = useTranslation();

  // Парсим список покупок из description, если он там есть
  const parseReceiptItems = (description: string): ReceiptItem[] | null => {
    if (!description || !description.includes('📋 Список покупок:')) return null;
    
    try {
      const lines = description.split('\n').slice(1); // Пропускаем заголовок
      const items: ReceiptItem[] = [];
      
      for (const line of lines) {
        if (!line.trim()) continue;
        
        // Формат: "• название x количество шт = сумма" или "• название = сумма"
        const match = line.match(/^[•\-]\s*(.+?)\s*(?:x\s*([\d.]+)\s*(?:шт|ед|kg|л)?)?\s*=\s*([\d.]+)$/);
        if (match) {
          const [, name, quantity, amount] = match;
          const qty = quantity ? parseFloat(quantity) : 1;
          const amt = parseFloat(amount);
          items.push({
            name: name.trim(),
            amount: amt,
            quantity: qty,
            unit_price: amt / qty
          });
        }
      }
      
      return items.length > 0 ? items : null;
    } catch (e) {
      console.error('Failed to parse receipt items:', e);
      return null;
    }
  };

  const initialReceiptItems = parseReceiptItems(transaction.description || '');
  const [editedTransaction, setEditedTransaction] = useState({
    ...transaction,
    receipt_items: initialReceiptItems || transaction.receipt_items
  });
  const [categories, setCategories] = useState<Category[]>([]);
  const [isUpdatingInternalTransfer, setIsUpdatingInternalTransfer] = useState(false);
  const [isUpdatingRecurring, setIsUpdatingRecurring] = useState(false);
  const [isUpdatingCategory, setIsUpdatingCategory] = useState(false);
  
  // Проверяем, является ли транзакция банковской (Plaid)
  const isPlaidTransaction = transaction.source === 'plaid' || !!transaction.plaid_transaction_id;

  // Блокируем скролл body когда модалка открыта
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    document.body.style.height = '100%';

    return () => {
      // Восстанавливаем скролл при закрытии
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.height = '';
    };
  }, []);

  // Обработка изменения флага регулярности для транзакций из Plaid
  const handleRecurringToggle = async (checked: boolean) => {
    if (!transaction.plaid_transaction_id || transaction.source !== 'plaid') {
      return;
    }

    setIsUpdatingRecurring(true);
    try {
      await apiClient.updatePlaidTransactionRecurring(
        transaction.plaid_transaction_id,
        checked
      );
      
      // Обновляем локальное состояние
      setEditedTransaction({
        ...editedTransaction,
        is_recurring: checked
      });
      
      // НЕ вызываем onUpdate для транзакций из Plaid, так как они не редактируются через обычный API
      // Просто закрываем модальное окно - родительский компонент перезагрузит данные при закрытии
      console.log('✅ Флаг регулярности обновлен:', checked);
      
      // Закрываем модальное окно после успешного обновления
      setTimeout(() => {
        onClose();
      }, 500);
    } catch (error) {
      console.error('❌ Ошибка обновления флага регулярности:', error);
      alert(t('transaction.updateRecurringFailed'));
    } finally {
      setIsUpdatingRecurring(false);
    }
  };

  // Обработка изменения категории для транзакций из Plaid
  const handlePlaidCategoryChange = async (newCategory: string) => {
    if (!transaction.plaid_transaction_id || transaction.source !== 'plaid') {
      // Для обычных транзакций просто обновляем состояние
      setEditedTransaction({ ...editedTransaction, category: newCategory });
      return;
    }

    setIsUpdatingCategory(true);
    try {
      await apiClient.updatePlaidTransactionCategory(
        transaction.plaid_transaction_id,
        newCategory
      );
      
      // Обновляем локальное состояние
      setEditedTransaction({
        ...editedTransaction,
        category: newCategory
      });
      
      console.log('✅ Категория банковской транзакции обновлена:', newCategory);
      
      // Закрываем модальное окно после успешного обновления
      setTimeout(() => {
        onClose();
      }, 500);
    } catch (error) {
      console.error('❌ Ошибка обновления категории:', error);
      alert('Не удалось обновить категорию. Попробуйте еще раз.');
    } finally {
      setIsUpdatingCategory(false);
    }
  };

  // Обработка изменения флага внутреннего перевода для транзакций из Plaid
  const handleInternalTransferToggle = async (checked: boolean) => {
    if (!transaction.plaid_transaction_id || transaction.source !== 'plaid') {
      return;
    }

    setIsUpdatingInternalTransfer(true);
    try {
      await apiClient.updatePlaidTransactionInternalTransfer(
        transaction.plaid_transaction_id,
        checked
      );
      
      // Обновляем локальное состояние
      setEditedTransaction({
        ...editedTransaction,
        is_internal_transfer: checked
      });
      
      // НЕ вызываем onUpdate для транзакций из Plaid, так как они не редактируются через обычный API
      // Просто закрываем модальное окно - родительский компонент перезагрузит данные при закрытии
      console.log('✅ Флаг внутреннего перевода обновлен:', checked);
      
      // Закрываем модальное окно после успешного обновления
      setTimeout(() => {
        onClose();
      }, 500);
    } catch (error) {
      console.error('❌ Ошибка обновления флага внутреннего перевода:', error);
      alert(t('transaction.updateInternalTransferFailed'));
    } finally {
      setIsUpdatingInternalTransfer(false);
    }
  };

  // Загружаем категории при монтировании компонента
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const allCategoriesData = await apiClient.getAllCategories();
        
        // Преобразуем системные категории в общий формат Category
        const systemCategories = (allCategoriesData.system_categories || []).map(cat => ({
          id: cat.db_name,
          name: cat.db_name,
          emoji: cat.emoji || null,
          is_system: true,
          type: 'both' as const
        }));
        
        // Преобразуем пользовательские категории в общий формат Category
        const userCategories = (allCategoriesData.user_categories || []).map(catName => ({
          id: catName,
          name: catName,
          emoji: null,
          is_system: false,
          type: 'both' as const
        }));
        
        // Объединяем все категории (сначала системные, потом кастомные)
        const allCategories = [
          ...systemCategories.sort((a, b) => a.name.localeCompare(b.name)),
          ...userCategories.sort((a, b) => a.name.localeCompare(b.name))
        ];
        
        setCategories(allCategories);
      } catch (error) {
        console.error('Ошибка загрузки категорий:', error);
      }
    };
    
    loadCategories();
  }, []);

  // Преобразуем список покупок обратно в description при сохранении
  const handleSave = () => {
    const updatedTransaction = { ...editedTransaction };
    
    if (updatedTransaction.receipt_items && updatedTransaction.receipt_items.length > 0) {
      // Форматируем список покупок
      const itemsList = updatedTransaction.receipt_items
        .map((item: ReceiptItem) => {
          let itemStr = `• ${item.name}`;
          if (item.quantity && item.quantity > 1) {
            itemStr += ` x ${item.quantity} шт`;
          }
          itemStr += ` = ${item.amount}`;
          return itemStr;
        })
        .join('\n');
      
      // Убираем старый список из description если есть
      let cleanDescription = updatedTransaction.description || '';
      if (cleanDescription.includes('📋 Список покупок:')) {
        cleanDescription = cleanDescription.split('📋 Список покупок:')[0].trim();
      }
      
      // Добавляем новый список
      updatedTransaction.description = cleanDescription 
        ? `${cleanDescription}\n\n📋 Список покупок:\n${itemsList}`
        : `📋 Список покупок:\n${itemsList}`;
      
      // Удаляем receipt_items перед отправкой, так как бэкенд не знает про это поле
      delete updatedTransaction.receipt_items;
    }
    
    onUpdate(updatedTransaction);
  };

  return (
    <div
      className="modal-overlay"
      onClick={onClose}
    >
      <div
        className="modal-content modal-content--edit-transaction"
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <h3 style={{ margin: 0, fontSize: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Icon name={editedTransaction.type === 'income' ? 'trending-up' : 'trending-down'} size={20} />
            {editedTransaction.type === 'income' ? t('transaction.income') : t('transaction.expense')}
          </h3>
          <button
            className="neo-raised"
            onClick={() => {
              if (window.confirm(t('transaction.deleteTransactionConfirm'))) {
                onDelete();
              }
            }}
            style={{
              padding: '8px 16px',
              background: 'var(--neo-bg)',
              color: '#FF3B30',
              border: 'none',
              borderRadius: '12px',
              fontSize: '14px',
              fontWeight: '700',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <Icon name="trash" size={16} />
            {t('common.delete', 'Удалить')}
          </button>
        </div>

        <div style={{ marginBottom: '24px' }} className="transaction-edit-modal__sections">
          {/* Предупреждение для Plaid — как в mobile */}
          {isPlaidTransaction && (
            <div className="transaction-edit-modal__plaid-warning">
              🏦 {t('transaction.bankTransactionLocked')}
            </div>
          )}

          {/* 1. Тип + Сумма — сверху как в mobile */}
          <div className="transaction-edit-modal__section">
            <div className="transaction-edit-modal__section-label">{t('transaction.amount')}</div>
            <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
              <button
                type="button"
                className={`transaction-edit-modal__type-chip ${editedTransaction.type === 'expense' ? 'transaction-edit-modal__type-chip--active transaction-edit-modal__type-chip--expense' : ''}`}
                onClick={() => !isPlaidTransaction && setEditedTransaction({ ...editedTransaction, type: 'expense' })}
                disabled={isPlaidTransaction}
              >
                {t('transaction.expense')}
              </button>
              <button
                type="button"
                className={`transaction-edit-modal__type-chip ${editedTransaction.type === 'income' ? 'transaction-edit-modal__type-chip--active transaction-edit-modal__type-chip--income' : ''}`}
                onClick={() => !isPlaidTransaction && setEditedTransaction({ ...editedTransaction, type: 'income' })}
                disabled={isPlaidTransaction}
              >
                {t('transaction.income')}
              </button>
            </div>
            <input
              className="neo-inset transaction-edit-modal__amount-input"
              type="number"
              step="0.01"
              value={editedTransaction.amount || ''}
              onChange={(e) => !isPlaidTransaction && setEditedTransaction({ ...editedTransaction, amount: parseFloat(e.target.value) || 0 })}
              disabled={isPlaidTransaction || !!(editedTransaction.receipt_items && editedTransaction.receipt_items.length > 0)}
              placeholder={t('transaction.enterAmount')}
            />
            {editedTransaction.receipt_items && editedTransaction.receipt_items.length > 0 && (
              <div className="transaction-edit-modal__hint">{t('transaction.amountCalculatedFromItems', 'Сумма из списка покупок')}</div>
            )}
          </div>

          {/* 2. Категория — как в mobile */}
          <div className="transaction-edit-modal__section">
            <label className="transaction-edit-modal__section-label">
              {t('transaction.category')}
              {isPlaidTransaction && <span className="transaction-edit-modal__hint-inline"> ({t('transaction.autoApply', 'применится автоматически')})</span>}
              {isUpdatingCategory && <span className="transaction-edit-modal__hint-inline transaction-edit-modal__hint-inline--loading">⏳ {t('common.saving', 'Сохранение...')}</span>}
            </label>
            <select
              className="neo-inset"
              value={editedTransaction.category || ''}
              onChange={(e) => handlePlaidCategoryChange(e.target.value)}
              disabled={isUpdatingCategory}
              style={{
                width: '100%',
                padding: '12px 40px 12px 12px',
                border: 'none',
                borderRadius: '12px',
                fontSize: '16px',
                background: 'var(--neo-bg)',
                color: 'var(--color-text-primary)',
                cursor: isUpdatingCategory ? 'not-allowed' : 'pointer',
                appearance: 'none',
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%23666' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 12px center',
                opacity: isUpdatingCategory ? 0.6 : 1,
              }}
            >
              <option value="">
                {editedTransaction.type === 'income' 
                  ? t('categories.selectIncome', 'Выберите категорию дохода...') 
                  : t('categories.selectExpense', 'Выберите категорию расхода...')}
              </option>
              
              {/* Системные категории */}
              {categories.filter(cat => cat.is_system).length > 0 && (
                <optgroup label={t('categories.system', '📋 Системные категории')}>
                  {categories
                    .filter(cat => cat.is_system)
                    .map(cat => (
                      <option key={cat.id} value={cat.name}>
                        {cat.emoji ? `${cat.emoji} ` : ''}{localizeCategory(cat.name)}
                      </option>
                    ))
                  }
                </optgroup>
              )}
              
              {/* Пользовательские категории */}
              {categories.filter(cat => !cat.is_system).length > 0 && (
                <optgroup label={t('categories.user', '✏️ Мои категории')}>
                  {categories
                    .filter(cat => !cat.is_system)
                    .map(cat => (
                      <option key={cat.id} value={cat.name}>
                        {cat.emoji ? `${cat.emoji} ` : ''}{localizeCategory(cat.name)}
                      </option>
                    ))
                  }
                </optgroup>
              )}
            </select>
          </div>

          {/* 3. Название — как в mobile (ModalSection titleLabel) */}
          <div className="transaction-edit-modal__section">
            <label className="transaction-edit-modal__section-label">{t('transaction.titleLabel')}</label>
            <input
              className="neo-inset transaction-edit-modal__field"
              type="text"
              value={editedTransaction.title || ''}
              onChange={(e) => setEditedTransaction({ ...editedTransaction, title: e.target.value })}
              placeholder={editedTransaction.type === 'income' ? t('transaction.titlePlaceholderIncome') : t('transaction.titlePlaceholderExpense')}
              disabled={isPlaidTransaction}
            />
          </div>

          {/* 4. Дата и Время */}
          <div className="transaction-edit-modal__section">
            <label className="transaction-edit-modal__section-label">{t('transaction.dateTime')}</label>
            <input
              className="neo-inset transaction-edit-modal__field"
              type="datetime-local"
              value={editedTransaction.date ? (() => {
                const d = new Date(editedTransaction.date);
                const year = d.getFullYear();
                const month = String(d.getMonth() + 1).padStart(2, '0');
                const day = String(d.getDate()).padStart(2, '0');
                const hours = String(d.getHours()).padStart(2, '0');
                const minutes = String(d.getMinutes()).padStart(2, '0');
                return `${year}-${month}-${day}T${hours}:${minutes}`;
              })() : ''}
              onChange={(e) => {
                if (!isPlaidTransaction) {
                  setEditedTransaction({ ...editedTransaction, date: e.target.value });
                }
              }}
              disabled={isPlaidTransaction}
            />
          </div>

          {/* 5. Список покупок из чека */}
          {editedTransaction.receipt_items && editedTransaction.receipt_items.length > 0 && (
            <div className="transaction-edit-modal__section">
              <ReceiptItemsEditor
                items={editedTransaction.receipt_items as ReceiptItem[]}
                currency={editedTransaction.currency || 'RUB'}
                onItemsChange={(newItems) => {
                  setEditedTransaction({ ...editedTransaction, receipt_items: newItems });
                }}
                onTotalAmountChange={(total) => {
                  setEditedTransaction({ ...editedTransaction, amount: total });
                }}
              />
            </div>
          )}

          {/* 6. Описание / Комментарий */}
          {(() => {
            // Убираем список покупок из описания, если он там есть
            let cleanDescription = editedTransaction.description || '';
            if (cleanDescription.includes('📋 Список покупок:')) {
              cleanDescription = cleanDescription.split('📋 Список покупок:')[0].trim();
            }
            
            // Показываем поле описания только если есть текст (без списка покупок)
            if (!cleanDescription && !editedTransaction.receipt_items?.length) {
              return null;
            }
            
            return (
              <div className="transaction-edit-modal__section">
                <label className="transaction-edit-modal__section-label">{t('transaction.additionalInfo', 'Описание')}</label>
                <textarea
                  className="neo-inset"
                  value={cleanDescription}
                  onChange={(e) => {
                    const newDesc = e.target.value;
                    setEditedTransaction({ ...editedTransaction, description: newDesc });
                  }}
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: 'none',
                    borderRadius: '12px',
                    fontSize: '16px',
                    resize: 'vertical',
                    background: 'var(--neo-bg)',
                    color: 'var(--color-text-primary)'
                  }}
                />
              </div>
            );
          })()}

          {/* UnifiedToggle — как в mobile */}
          {transaction.source === 'plaid' && transaction.plaid_transaction_id && (
            <>
              <div className="transaction-edit-modal__section transaction-edit-modal__toggle-row">
                <UnifiedToggle
                  value={editedTransaction.is_internal_transfer || false}
                  onValueChange={handleInternalTransferToggle}
                  disabled={isUpdatingInternalTransfer}
                  label={t('transaction.internalTransfer')}
                  description={t('transaction.internalTransferHint')}
                />
              </div>
              <div className="transaction-edit-modal__section transaction-edit-modal__toggle-row">
                <UnifiedToggle
                  value={editedTransaction.is_recurring || false}
                  onValueChange={handleRecurringToggle}
                  disabled={isUpdatingRecurring}
                  label={t('transaction.recurring')}
                  description={t('transaction.recurringHint', 'Подписка, ежемесячный платёж и т.д.')}
                />
              </div>
            </>
          )}
        </div>

        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            className="neo-raised"
            onClick={onClose}
            style={{
              flex: 1,
              padding: '14px',
              background: 'var(--neo-bg)',
              color: '#FF3B30',
              border: 'none',
              borderRadius: '14px',
              fontSize: '16px',
              fontWeight: '700',
              cursor: 'pointer'
            }}
          >
            {t('common.cancel')}
          </button>
          <button
            className="neo-raised"
            onClick={handleSave}
            disabled={isSubmitting}
            style={{
              flex: 1,
              padding: '14px',
              background: 'var(--neo-bg)',
              color: editedTransaction.type === 'income' ? '#34C759' : '#FF3B30',
              border: 'none',
              borderRadius: '14px',
              fontSize: '16px',
              fontWeight: '700',
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              opacity: isSubmitting ? 0.6 : 1
            }}
          >
            {isSubmitting ? t('common.saving', 'Сохранение...') : t('common.save')}
          </button>
        </div>
      </div>
    </div>
  );
};

